import { useState } from 'react'
import { newId } from '@/lib/ids'
import type { TagEntry } from '../../commands'
import { guardFor } from '../../data/useGate'
import { draftPayloads } from '../../model/delivery'
import { Btn, Cta, Footer, Icon, ItemRow, Strip, Tick, V1Screen, WaitCta } from '../kit'
import { C } from '../style'
import { LiveScanner, Refused, TypeCodeSheet } from '../scan'
import { useCheckedScan } from '../scanCheck'
import { tr, shortCode } from '../text'
import { Rich } from '../Rich'
import { StepHeader } from './shared'
import { useV1 } from './ctx'

/** F4.7 — नमूना सील करें: close the sample bag in front of him, scan the seal, into the box. */
export function SealScreen({ onBack, onNext }: { onBack: () => void; onNext: () => void }) {
  const { d, dispatch, roster, session, used, stamp, otherCodes } = useV1()
  const [seal, setSeal] = useState<{ payload: string; entry: TagEntry } | null>(
    d.sample ? { payload: d.sample.seal_payload, entry: d.sample.seal_entry } : null,
  )
  const [err, setErr] = useState<string | null>(null)
  const [typing, setTyping] = useState(false)
  // A seal already saved on this draft is not "used" for the guard.
  const inDraft = draftPayloads(d)
  if (d.sample) inDraft.delete(d.sample.seal_payload)
  const guard = guardFor(session, roster, used, 'SEAL', inDraft, otherCodes)
  const check = useCheckedScan({
    guard,
    onAccept: (payload, entry) => setSeal({ payload, entry }),
    onRefuse: (scan) => dispatch({ type: 'refuse_scan', scan }),
  })

  const save = () => {
    if (!seal) return
    if (d.sample?.seal_payload !== seal.payload) {
      dispatch({
        type: 'set_sample',
        sample: {
          id: newId(),
          seal_payload: seal.payload,
          seal_entry: seal.entry,
          weight_g: null,
          collected_at: stamp(),
          photo_media_id: null,
          underweight_allowed_by: null,
        },
      })
    }
    onNext()
  }

  return (
    <V1Screen
      main={{ gap: 9 }}
      header={
        <StepHeader step="sample" icon="seal" title={tr('gate.v1.seal.title')} onBack={onBack} />
      }
      footer={
        <Footer>
          {seal ? (
            <Cta label={tr('gate.v1.seal.save')} icon="ok" onClick={save} />
          ) : (
            <WaitCta>{tr('gate.v1.seal.hold')}</WaitCta>
          )}
        </Footer>
      }
      overlay={
        typing ? (
          <TypeCodeSheet
            title={tr('gate.v1.seal.typeTitle')}
            check={check}
            onClose={() => setTyping(false)}
          />
        ) : null
      }
    >
      <Strip icon="user" iconBox>
        <Rich text={tr('gate.v1.seal.inFront')} />
      </Strip>
      {/* Scanning again replaces the seal (a torn one is simply not used). */}
      <LiveScanner
        icon="seal"
        hint={tr('gate.v1.seal.hold')}
        minHeight={170}
        check={check}
        onError={setErr}
      />
      {/* No printed seal to scan, or the QR will not read: type the number on the strip. */}
      <Btn icon="keypad" onClick={() => setTyping(true)}>
        {tr('gate.v1.tag.type')}
      </Btn>
      {err && <Refused text={err} />}
      {seal && (
        <ItemRow
          border={C.ok}
          borderWidth={2}
          padding="6px 10px"
          lead={<Icon name="seal" size={30} style={{ color: C.ok }} />}
          titleSize={19}
          title={tr('gate.v1.seal.code', { code: shortCode(seal.payload) })}
          line={<span style={{ fontSize: 15, color: C.ok }}>{tr('gate.v1.seal.scanned')}</span>}
          right={<Tick size={28} />}
        />
      )}
    </V1Screen>
  )
}
